import React, { useState } from 'react'
import { AppShell } from '../layout/AppShell'
import StandaloneInput from '../../components/Form/StandaloneInput'
import { graphql } from '../../gql'
import { useQuery } from 'urql'
import StatusPill from '../../components/Badges/StatusPill'
import {
  getBadgeColor,
  getTimeAgoBadgeColor,
} from '../scanning/ScanHistoryTable.util'
import { formatShortRelative } from '../../utils/date'
import { useDebounce } from 'usehooks-ts'
import { RetireParcelModal } from './RetireParcelModal'
import { MagnifyingGlassIcon } from '@heroicons/react/20/solid'
import LastScannedSelect, { LastScannedType } from './LastScannedSelect'
import CyclesSelect, { CyclesType } from './CyclesSelect'
import { BagCyclesEnum, LastScannedAtEnum } from '../../gql/graphql'

const ParcelsQuery = graphql(`
  query Parcels(
    $first: Int
    $after: String
    $search: String
    $retired: Boolean
    $cycles: [BagCyclesEnum]
    $cyclesGt: Int
    $cyclesLt: Int
    $lastScanned: [LastScannedAtEnum]
    $lastScannedAtGt: DateTime
    $lastScannedAtLt: DateTime
  ) {
    allBags(
      first: $first
      after: $after
      search: $search
      retired: $retired
      cycles: $cycles
      cyclesGt: $cyclesGt
      cyclesLt: $cyclesLt
      lastScannedAt: $lastScanned
      lastScannedAtGt: $lastScannedAtGt
      lastScannedAtLt: $lastScannedAtLt
    ) {
      edges {
        node {
          id
          bagId
          cycles
          status
          createdAt
          updatedAt
          lastScannedAt
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`)

const Parcels: React.FC = () => {
  const [search, setSearch] = useState('')
  const [lastScannedTypes, setLastScannedTypes] = useState<LastScannedType[]>(
    []
  )
  const [cyclesTypes, setCyclesTypes] = useState<CyclesType[]>([])
  const [retireParcelId, setRetireParcelId] = useState<string | null>(null)
  const debouncedSearch = useDebounce(search, 500)

  const [{ data }] = useQuery({
    query: ParcelsQuery,
    variables: {
      search: debouncedSearch,
      first: 10,
      retired: false,
      cycles: cyclesTypes.map(x => BagCyclesEnum[x]),
      lastScanned: lastScannedTypes.map(x => LastScannedAtEnum[x]),
    },
  })

  const parcels =
    data?.allBags?.edges.map(edge => edge?.node).filter(x => !!x) ?? []

  return (
    <AppShell>
      <h1 className={'text-3xl leading-9 font-bold mb-4'}>Parcels</h1>

      <div className="bg-white border-gray-200 border sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex justify-between gap-20 items-center">
            <div id="search-wrapper" className={'flex-1 relative'}>
              <div
                className={
                  'absolute left-0 top-0 bottom-0 pl-4 flex items-center'
                }
              >
                <MagnifyingGlassIcon className={'w-5 h-5 text-gray-500'} />
              </div>
              <StandaloneInput
                value={search}
                onChange={e => setSearch(e.target.value)}
                className={'h-14 w-100 pl-11'}
                placeholder={'Search for parcels by ID'}
              />
            </div>
            <div
              id="filters"
              className={'flex-1 flex justify-end items-center gap-4'}
            >
              <span className={'text-sm text-gray-500 leading-5'}>
                Filter by
              </span>
              <CyclesSelect value={cyclesTypes} onChange={setCyclesTypes} />
              <LastScannedSelect
                value={lastScannedTypes}
                onChange={value => {
                  setLastScannedTypes(value)
                }}
              />
            </div>
          </div>
          <div className="mt-8 flow-root">
            <div className="-mx-4 -my-2 overlow-x-auto sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead>
                    <tr>
                      <th
                        scope="col"
                        className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0"
                      >
                        ID
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        Cycles
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        Last scan
                      </th>
                      <th
                        scope="col"
                        className="relative py-3.5 pl-3 pr-4 sm:pr-0"
                      >
                        <span className="sr-only">Delete</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {parcels.map(parcel => (
                      <tr key={parcel!.id}>
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm text-gray-500 sm:pl-0">
                          {parcel!.bagId}
                        </td>
                        <td className={'text-sm text-gray-500 leading-5 px-3'}>
                          <StatusPill
                            color={getBadgeColor(parcel!.cycles ?? 0)}
                          >
                            {(parcel!.cycles ?? 0) > 200
                              ? '200+'
                              : parcel!.cycles}
                          </StatusPill>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {parcel?.lastScannedAt && (
                            <StatusPill
                              color={getTimeAgoBadgeColor(
                                new Date(parcel!.lastScannedAt)
                              )}
                            >
                              {formatShortRelative(
                                new Date(parcel!.lastScannedAt),
                                new Date()
                              )}
                            </StatusPill>
                          )}
                        </td>
                        <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-0">
                          <button
                            className={'text-red-500'}
                            onClick={() => setRetireParcelId(parcel!.id)}
                          >
                            Retire
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
      <RetireParcelModal
        parcelId={retireParcelId}
        isOpen={!!retireParcelId}
        onClose={() => {
          setRetireParcelId(null)
        }}
        onParcelDeleted={() => {
          setRetireParcelId(null)
        }}
      />
    </AppShell>
  )
}

export default Parcels
